import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Input, message } from 'antd';
import { ApartmentOutlined, ArrowLeftOutlined, GlobalOutlined, HomeOutlined, SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { postRequest } from '../../appRedux/actions/Axios';
import { GetCompany, SetCompanies } from '../../appRedux/actions/Selector/Company';
import { selectCompanyPaymentWarning, setCompanyPaymentBlocked, setEncriptionBranch, setEncriptionCompany, userSignIn } from '../../appRedux/actions/Auth';
import { isCompanyAccessError, isSameCompanySession } from '../../util/companyPaymentAccess';
import CircularProgress from 'components/CircularProgress/index';
import LoginHeader from '../Topbar/LoginHeader/index';
import CardSelector from './CardSelector';
import './companySelector.less';

const companyFilters = [
  { key: '1', label: 'Todas', icon: <ApartmentOutlined /> },
  { key: '2', label: 'Nacionales', icon: <HomeOutlined /> },
  { key: '3', label: 'Internacionales', icon: <GlobalOutlined /> }
];

const CompanySelector = ({ location }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { authUser, companyGroup, blockedCompany } = useSelector(({ auth }) => auth);
  const { companyList } = useSelector(({ Company }) => Company);
  const [searchCriteria, setSearchCriteria] = useState('');
  const [currentFilter, setCurrentFilter] = useState('1');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const autoSelectionAttempted = useRef(false);
  const selectionInProgress = useRef(false);
  const mounted = useRef(true);
  const companyGroupId = location.state?.CompanyGroup || companyGroup?.id;

  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let isActive = true;

    if (!authUser) {
      history.replace('/signin');
      return undefined;
    }

    if (!companyGroupId) {
      history.replace('/companyGroup');
      return undefined;
    }

    autoSelectionAttempted.current = false;
    selectionInProgress.current = false;
    setSelectedCompanyId(null);
    setIsLoading(true);
    dispatch(SetCompanies([]));

    dispatch(GetCompany({ CompanyGroupId: companyGroupId }))
      .then(() => {
        if (isActive) setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        if (isActive) {
          setIsLoading(false);
          message.error('No pudimos cargar tus compañías. Inténtalo nuevamente.');
        }
      });

    return () => {
      isActive = false;
    };
  }, [authUser, companyGroupId, dispatch, history]);

  const handleSelectCompany = useCallback(company => {
    if (selectionInProgress.current) return;

    selectionInProgress.current = true;
    setSelectedCompanyId(company.id);
    const requestToken = localStorage.getItem('token');
    dispatch(postRequest('/users/SelectCompany', { Company_ID: company.id }, {}))
      .then(response => {
        if (!mounted.current || !isSameCompanySession(requestToken, localStorage.getItem('token'))) return;
        const { payload: { data } } = response;
        if (typeof data !== 'undefined') {
          dispatch(setCompanyPaymentBlocked(null));
          dispatch(setEncriptionBranch(null));
          dispatch(setEncriptionCompany(company));
          dispatch(userSignIn(data));
          dispatch(selectCompanyPaymentWarning(company.id, data.isPaymentWarning, data.token));
          history.push({
            pathname: '/branchSelector',
            state: { Company_ID: company.id }
          });
        } else {
          selectionInProgress.current = false;
          setSelectedCompanyId(null);
          message.error('No pudimos abrir esta compañía. Inténtalo nuevamente.');
        }
      })
      .catch(error => {
        if (!mounted.current) return;
        selectionInProgress.current = false;
        setSelectedCompanyId(null);
        if (isCompanyAccessError(error.error || error)) return;
        console.log(error);
        message.error('No pudimos abrir esta compañía. Inténtalo nuevamente.');
      });
  }, [dispatch, history]);

  useEffect(() => {
    if (!isLoading && companyList.length === 1 && !autoSelectionAttempted.current &&
      companyList[0].id !== blockedCompany?.companyId) {
      autoSelectionAttempted.current = true;
      handleSelectCompany(companyList[0]);
    }
  }, [blockedCompany?.companyId, companyList, handleSelectCompany, isLoading]);

  const normalizedSearch = searchCriteria.trim().toLocaleUpperCase();
  const filteredCompanies = companyList.filter(company => {
    const matchesName = (company.displayCompany || '').toLocaleUpperCase().includes(normalizedSearch);

    if (currentFilter === '2') return matchesName && company.isLocal === true;
    if (currentFilter === '3') return matchesName && company.isLocal === false;
    return matchesName;
  });
  const hasActiveFilter = normalizedSearch.length > 0 || currentFilter !== '1';
  const hasLargeCompanyCards = companyList.length <= 12;

  return (
    <div className={`dasi-company-selector${hasLargeCompanyCards ? ' dasi-company-selector--large-cards' : ''}`}>
      <LoginHeader compact />

      <main className="dasi-company-selector__main">
        <header className="dasi-company-selector__hero">
          <div>
            <button
              type="button"
              className="dasi-company-selector__back"
              onClick={() => history.push('/companyGroup')}>
              <ArrowLeftOutlined />
              <span>Volver al grupo</span>
            </button>
            <span className="dasi-company-selector__step">Paso 2 de 3</span>
            <h1>Elige una compañía</h1>
          </div>
        </header>

        <section className="dasi-company-selector__panel" aria-label="Compañías disponibles">
          <div className="dasi-company-selector__toolbar">
            <Input
              value={searchCriteria}
              onChange={event => setSearchCriteria(event.target.value)}
              prefix={<SearchOutlined />}
              placeholder="Buscar por nombre"
              allowClear
              size="large"
              aria-label="Buscar compañía"
            />

            <div className="dasi-company-selector__filters" role="group" aria-label="Filtrar compañías">
              {companyFilters.map(filter => (
                <React.Fragment key={filter.key}>
                  <button
                    type="button"
                    aria-pressed={currentFilter === filter.key}
                    className={currentFilter === filter.key ? 'is-active' : ''}
                    onClick={() => setCurrentFilter(filter.key)}>
                    {filter.icon}
                    <span>{filter.label}</span>
                  </button>
                  {filter.key === '1' && (
                    <span className="dasi-company-selector__filter-count" aria-label={`${companyList.length} compañías disponibles`}>
                      <strong>{companyList.length}</strong>
                      <span>{companyList.length === 1 ? 'compañía' : 'compañías'}</span>
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="dasi-company-selector__loading" aria-live="polite">
              <CircularProgress />
              <p>Preparando tus compañías…</p>
            </div>
          ) : filteredCompanies.length > 0 ? (
            <div className="dasi-company-selector__grid">
              {filteredCompanies.map(company => (
                <CardSelector
                  key={company.id}
                  Entity={company}
                  SelectEntity={handleSelectCompany}
                  type={1}
                  disabled={selectedCompanyId !== null}
                  isSelected={selectedCompanyId === company.id}
                />
              ))}
            </div>
          ) : (
            <div className="dasi-company-selector__empty">
              <span><SearchOutlined /></span>
              <h2>{hasActiveFilter ? 'No encontramos coincidencias' : 'No hay compañías disponibles'}</h2>
              <p>
                {hasActiveFilter
                  ? 'Prueba con otro nombre o selecciona un filtro diferente.'
                  : 'Cuando tengas una compañía asignada, aparecerá aquí.'}
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default CompanySelector;
